import React from 'react';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { DrawerHeader } from './components/drawer-header';
import { DrawerMain } from './components/drawer-main';
import { NavMenuItem } from '../app-bar';
import { Logo } from '../shared/logo';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
import { useLocation } from 'react-router-dom';
interface DrawerProps {
  theme: Theme;
  drawerWidth: number;
  drawerOpen: boolean;
}

interface LeftDrawerProps extends DrawerProps {
  primaryNavItems: NavMenuItem[];
  secondaryNavItems: NavMenuItem[];
  toggleDrawer: () => void;
  handleRouteChange: (route: string) => void;
}

export function LeftDrawer(props: LeftDrawerProps) {
  const { theme, drawerWidth, drawerOpen } = props;
  const isSmallScreen = useIsSmallScreen();
  const { pathname } = useLocation();
  // Track the currently selected route
  const [selectedItem, setSelectedItem] = React.useState<string>('');
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});

  const normalizedPath = React.useCallback((path: string) => {
    return path.startsWith('/') ? path : `/${path}`;
  }, []);

  const isActivePath = React.useCallback(
    (path: string) => {
      const normalized = normalizedPath(path);
      return pathname === normalized || pathname.startsWith(`${normalized}/`);
    },
    [normalizedPath, pathname],
  );

  const hasActiveChild = React.useCallback(
    (item: NavMenuItem, testPath: string) => {
      return (
        item.children?.some((child) => {
          const normalized = normalizedPath(child.path);
          return (
            testPath === normalized || testPath.startsWith(`${normalized}/`)
          );
        }) ?? false
      );
    },
    [normalizedPath],
  );

  React.useEffect(() => {
    setSelectedItem(pathname);
    props.primaryNavItems.forEach((item) => {
      if (hasActiveChild(item, pathname)) {
        setOpenItems((current) =>
          current[item.path] ? current : { ...current, [item.path]: true },
        );
      }
    });
  }, [hasActiveChild, pathname, props.primaryNavItems]);

  function handleItemClick(item: NavMenuItem) {
    if (item.children?.length) {
      setOpenItems((current) => ({
        ...current,
        [item.path]: !current[item.path],
      }));
      return;
    }

    setSelectedItem(normalizedPath(item.path));
    props.handleRouteChange(normalizedPath(item.path));
    if (isSmallScreen && drawerOpen) {
      props.toggleDrawer();
    }
  }

  function renderNavItem(item: NavMenuItem, level = 0) {
    const hasChildren = !!item.children?.length;
    const isOpen = !!openItems[item.path];
    const isSelected = hasChildren
      ? isActivePath(item.path) || hasActiveChild(item, pathname)
      : selectedItem === normalizedPath(item.path);

    return (
      <React.Fragment key={item.path}>
        <ListItem disablePadding sx={{ marginBottom: '5px' }}>
          <ListItemButton
            aria-label={item.ariaLabel}
            aria-selected={isSelected}
            sx={{
              ':hover': { bgcolor: 'lightblue' },
              borderLeft: level > 0 ? '4px solid transparent' : undefined,
              '&.Mui-selected, &.MuiListItemIcon-root, &.Mui-selected:hover': {
                backgroundColor: level === 0 ? '#36AFA5' : '#aee2dc',
                borderLeft:
                  level > 0 ? '5px solid #238f86' : '4px solid transparent',
                color: level === 0 ? 'white' : '#134f49',
              },
              '&.Mui-selected .MuiListItemIcon-root': {
                color: level === 0 ? 'white' : '#36AFA5',
              },
              '&.Mui-selected .MuiListItemText-primary': {
                fontWeight: level === 0 ? 400 : 700,
              },
              pl: 2 + level * 2,
            }}
            selected={isSelected}
            onClick={() => handleItemClick(item)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
            {hasChildren ? isOpen ? <ExpandLess /> : <ExpandMore /> : null}
          </ListItemButton>
        </ListItem>
        {hasChildren ? (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) => renderNavItem(child, level + 1))}
            </List>
          </Collapse>
        ) : null}
      </React.Fragment>
    );
  }

  return (
    <DrawerMain
      variant="permanent"
      theme={theme}
      open={drawerOpen}
      width={drawerWidth}
    >
      <DrawerHeader theme={theme}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 36,
            width: '100%',
            marginTop: '2px',
          }}
        >
          <div style={{ width: '38%', height: '100%' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              // onClick={() => navigate('/properties')}
            >
              <IconButton
                aria-label="Embue"
                onClick={() => props.handleRouteChange('/properties')}
              >
                <Logo />
              </IconButton>
            </div>
          </div>
        </Box>
        <IconButton aria-label="close menu" onClick={props.toggleDrawer}>
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List
        component="nav"
        sx={
          isSmallScreen
            ? {
                marginBottom: '75px',
              }
            : null
        }
      >
        {props.primaryNavItems.map((item) => renderNavItem(item))}
      </List>
    </DrawerMain>
  );
}
